<script lang="ts">
  import { nrkClose } from '@nrk/core-icons';
  import { postFeedback } from '../../api/feedback';
  import { clientState } from '../../util/clientStateStore';
  import { i18n } from '../../util/i18n/i18n';
  import { trackEvent } from '../../util/snowplow';
  import { getItem } from '../../util/storage';
  import { getSubmittedVotesKey } from '../../util/votesKeys';
  import FocusText from '../FocusText.svelte';
  import CoreDialog from '../core/CoreDialog.svelte';
  import FeedbackForm from './FeedbackForm.svelte';

  let submitting = false;

  const getVotes = (submittedVotesKey: string) => {
    return getItem<string[]>('local', submittedVotesKey) ?? [];
  };

  const onClickSend = async () => {
    const submittedVotesKey = getSubmittedVotesKey($clientState.id);
    const votes = getVotes(submittedVotesKey);
    submitting = true;
    try {
      await postFeedback(
        {
          comment,
          votesFeedback: emoji,
          category,
          hasVoted: votes.length !== 0,
          stateTypeId: `${$clientState.type}-${$clientState.id}`,
        },
        { fid }
      );
      trackEvent({ action: 'click:feedback-submit' });
    } catch {
      trackEvent({ action: 'error:feedback-submit' });
    } finally {
      submitting = false;

      comment = '';
      emoji = -1;
      state = 'sent';
      fid = null;
    }
  };

  const onClickCancel = () => {
    trackEvent({ action: 'click:feedback-close' });
    feedbackDialog.close();
  };

  export let feedbackDialog: CoreDialog;

  let state: 'closed' | 'open' | 'sent' = 'closed';

  let comment = '';
  let emoji = -1;
  let category = '';
  let fid: string | null = null;

  let emojiTimeout: number | undefined;
  const debounceEmoji = (newEmoji: number) => {
    const submittedVotesKey = getSubmittedVotesKey($clientState.id);
    const votes = getVotes(submittedVotesKey);
    if (emojiTimeout) {
      clearTimeout(emojiTimeout);
    }
    if (emoji !== -1) {
      emojiTimeout = window.setTimeout(async () => {
        const response = await postFeedback(
          {
            category,
            votesFeedback: newEmoji,
            hasVoted: votes.length !== 0,
            stateTypeId: `${$clientState.type}-${$clientState.id}`,
          },
          { fid }
        );
        if (response) {
          fid = response.fid;
        }
      }, 5000);
    }
  };

  const toggleGlobalScroll = (currentState: typeof state) => {
    if (currentState !== 'closed') {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  };

  $: sendDisabled = comment === '' && emoji === -1;
  $: debounceEmoji(emoji);
  $: toggleGlobalScroll(state);
</script>

<CoreDialog
  id="feedback-dialog"
  bind:this={feedbackDialog}
  label={i18n('feedback.dialog.heading')}
  on:open={() => {
    state = 'open';
  }}
  on:close={() => {
    state = 'closed';
  }}
>
  <div class="content">
    {#if state === 'open'}
      <h1 class="sr-only">{i18n('feedback.dialog.heading')}</h1>
      <FeedbackForm
        bind:group={emoji}
        bind:comment
        bind:category
        onSubmit={onClickSend}
      >
        <div class="action-container">
          <button class="back" on:click={onClickCancel} type="button"
            >{i18n('feedback.dialog.cancel-button')}
          </button>
          <button
            class="send secondary"
            disabled={sendDisabled}
            aria-busy={submitting}
          >
            {i18n('feedback.dialog.send-button')}
          </button>
        </div>
      </FeedbackForm>
    {:else if state === 'sent'}
      <FocusText elementType="h2" styling="margin: var(--spacing-32) 0;">
        <span>
          {i18n('feedback.dialog.confirmation-text')}
        </span>
      </FocusText>
      <div class="sent-container">
        <button class="secondary" on:click={onClickCancel}
          >{i18n('feedback.dialog.back-button')}</button
        >
      </div>
    {/if}
  </div>
  <button
    aria-label={i18n('feedback.dialog.back-button')}
    class="close-button"
    on:click={onClickCancel}
  >
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    <div class="close-icon">{@html nrkClose}</div>
  </button>
</CoreDialog>

<style>
  h1 {
    margin-bottom: var(--spacing-8);
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: var(--spacing-16) auto;
    padding: var(--spacing-16);
    max-width: 28rem;
  }

  .action-container {
    display: flex;
    justify-content: flex-end;
    margin: 1rem 0;
    padding-right: 1rem;
  }

  .action-container > :global(button) {
    margin: 0 var(--spacing-8);
  }

  .sent-container {
    margin: 1rem 0;
  }
  .back {
    color: var(--color-cart-text);
    background-color: transparent;
    text-decoration: underline;
  }

  .close-button {
    background: transparent;
    position: absolute;
    top: 0;
    right: 0;
    transition: transform 0.1s;
  }

  .send {
    background: var(--color-feedback-button-bg);
    color: black;
    border-radius: unset;
  }

  .close-icon:hover {
    transform: scale(1.1);
  }
</style>
